
  import { router } from '@/ui/routes';
  import {
    computed,
    defineComponent,
    ref,
    Ref,
    onBeforeMount,
    onBeforeUnmount,
    provide,
    readonly,
    onUnmounted,
  } from 'vue';
  import { Loader } from '@/ui/components/Loader';
  import AnalyticsBuilderHeader from '@/ui/components/analytics/AnalyticsBuilderHeader.vue';
  import { useReportRecord } from '@/ui/concerns/analytics/useReportRecord';
  import AnalyticsBuilderOptions from '@/ui/components/analytics/AnalyticsBuilderOptions.vue';
  import AnalyticsFiltersSidebar from '@/ui/components/analytics/sidebar/AnalyticsFiltersSidebar.vue';
  import AnalyticsSendSidebar from '@/ui/components/analytics/sidebar/AnalyticsSendSidebar.vue';
  import {
    ReportFilter,
    ReportRecordData as Report,
    ReportWidget,
  } from '@/services/api/resources/analytics-builder/model';
  import isEqual from 'lodash/isEqual';
  import { hasPermission, Permissions, user } from '@/ui/auth/auth';
  import { useAnalyticsActions, useAnalyticsBuilder } from '@/ui/concerns/analytics/useAnalytics';
  import { required, Validator } from '@/entities/validation';
  import { toasted } from '@/ui/helpers';
  import { validationError } from '@/ui/dictionary';
  import { t, locales } from '@/ui/i18n/i18n';
  import { ForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
  import { dummyTask, useSetLocale } from '@/ui/concerns';
  import Widget from '@/ui/components/analytics/Widget.vue';
  import WidgetAdd from '@/ui/components/analytics/widget/WidgetAdd.vue';
  import emitter from '@/services/emitter';
  import { LineChartTimeGroupingType } from '@/services/api/resources/analytics-builder/constants';
  import {
    reportFromAnalyticsPageKey,
    reportIdFromAnalyticsPageKey,
    selectedWidgetFromAnalyticsPageKey,
    isGuestFromAnalyticsPageKey,
  } from '@/ui/injectionKeys';
  import { useReportTemplateStore } from '@/stores/analytics/reportTemplate';
  import { useFormItemsStore } from '@/stores/analytics/formItems';
  import AnalyticsShareSidebar from '@/ui/components/analytics/sidebar/AnalyticsShareSidebar.vue';
  import GuestFooter from '@/ui/components/analytics/GuestFooter.vue';
  import { useModal } from '@/ui/concerns/modals/useModal';

  interface AnalyticsBuilderProps {
    id?: number;
    isGuest?: boolean;
    locale?: string;
  }

  export default defineComponent<AnalyticsBuilderProps>({
    components: {
      GuestFooter,
      AnalyticsShareSidebar,
      Widget,
      WidgetAdd,
      ForbiddenPage,
      AnalyticsFiltersSidebar,
      AnalyticsSendSidebar,
      AnalyticsBuilderOptions,
      AnalyticsBuilderHeader,
      Loader,
    },
    props: ['id', 'isGuest', 'locale'],
    setup(props) {
      if (props.isGuest) {
        useSetLocale(props.locale as locales | undefined);
      }

      const store = useReportTemplateStore();
      const { emptyReport } = useReportRecord();
      const reportId = ref<number | undefined>(props.id);
      const report: Ref<Report> = ref(store.template ?? emptyReport());
      const reportPreviousState = ref<Report | undefined>();
      const reportVersion = ref('');
      const [isFilterOpen, onFilterOpen, filterClose] = useModal();
      const [isSendOpen, onSendOpen, sendClose] = useModal();
      const [isShareOpen, onShareOpen, onShareClose] = useModal();
      const selectedWidgetFilter = ref<ReportWidget | null>(null);
      const isScheduleDirty = ref<boolean>(false);

      const widgets = computed(() => report.value.widgets ?? []);
      const isCreatingMode = computed<boolean>(() => !reportId.value);

      const validateTitle: Validator<Report> = record => [
        ...required(record.title, 'reportTitle'),
      ];

      const {
        createRecord,
        updateRecord,
        deleteRecord,
        getReport,
        getPublicReport,
        getApprovalStatuses,
      } = useAnalyticsActions(reportId, report, reportPreviousState, reportVersion);

      getApprovalStatuses(props.isGuest).run();

      const {
        onMetricChanged,
        onAttributeChanged,
        onTitleChange,
        onPercentageToggled,
        onDisplayChange,
        onAddWidgetClicked,
        onLineChartTimeGroupingChange,
        onDeleteCard,
        onAddCardAfter,
        visibleWidgets,
        resetVisibleWidgets,
      } = useAnalyticsBuilder(report);

      const onFilterClose = () => {
        selectedWidgetFilter.value = null;
        filterClose();
      };

      const onFilterApply = (filters: ReportFilter) => {
        isFilterOpen.value = false;

        if (selectedWidgetFilter.value) {
          if (!isEqual(selectedWidgetFilter.value.filters, filters)) {
            selectedWidgetFilter.value.filters = filters;
          }

          selectedWidgetFilter.value = null;
          return;
        }

        if (!isEqual(report.value.filters, filters)) {
          resetVisibleWidgets();
          report.value.filters = filters;
        }
      };

      const onWidgetFilterOpen = (widgetKey: string) => {
        if (isFilterOpen.value) {
          return;
        }

        const widget = widgets.value.find(widget => widget.key === widgetKey);

        if (!widget) {
          return;
        }

        selectedWidgetFilter.value = widget;
        isFilterOpen.value = true;
      };

      const onSendClose = () => {
        selectedWidgetFilter.value = null;
        sendClose();
      };

      const onSaveSaved = () => {
        isSendOpen.value = false;
        isScheduleDirty.value = true;
      };

      const onSaveCleared = () => {
        report.value.schedule = null;
        isScheduleDirty.value = false;
      };

      const onDelete = () => {
        deleteRecord.run();
      };

      const onSave = (quit: boolean) => {
        const errors = validateTitle(report.value);

        if (errors.length > 0) {
          errors.forEach(error => toasted.error(validationError(t('common.title'), error)));
          return;
        }

        const saveCallback = () => {
          quit && router.push({ name: 'analytics-v2' });
        };

        if (isCreatingMode.value) {
          createRecord.run().then(saveCallback);
          return;
        }

        if (!isDirty() && !isScheduleDirty.value) {
          saveCallback();
          return;
        }

        updateRecord.run().then(() => {
          isScheduleDirty.value = false;
          saveCallback();
        });
      };

      const taskGetReport = computed(() => !reportId.value
        ? dummyTask
        : (props.isGuest ? getPublicReport : getReport));
      const isSaving = computed<boolean>(() => createRecord.state.value.pending || updateRecord.state.value.pending);
      const isDeleting = computed<boolean>(() => deleteRecord.state.value.pending);
      const isDirty = () => !isEqual(report.value, reportPreviousState.value);
      const canSave = computed<boolean>(() => {
        if (isCreatingMode.value) {
          return true;
        }

        return (isDirty() || isScheduleDirty.value) && !isSaving.value && !isDeleting.value;
      });

      const canDelete = computed<boolean>(() => {
        if (isCreatingMode.value) {
          return false;
        }

        return !isSaving.value && !isDeleting.value;
      });

      const isUserViewOnly = computed<boolean>(() => {
        return !(hasPermission(Permissions.MANAGE_ALL_ANALYTICS) || hasPermission(Permissions.MANAGE_OWN_ANALYTICS));
      });

      const hideDelete = computed<boolean>(() => isCreatingMode.value || isUserViewOnly.value);

      const canUserSeeReport = computed<boolean>(() => {
        if (props.isGuest) {
          return true;
        }

        if (isCreatingMode.value) {
          return hasPermission(Permissions.MANAGE_OWN_ANALYTICS) || hasPermission(Permissions.MANAGE_ALL_ANALYTICS);
        }

        if (hasPermission(Permissions.VIEW_ALL_ANALYTICS) || hasPermission(Permissions.MANAGE_ALL_ANALYTICS)) {
          return true;
        }

        return hasPermission(Permissions.MANAGE_OWN_ANALYTICS) && report.value.user.id === user.value.id;
      });

      const isAddWidgetVisible = computed<boolean>(() => {
        return !isUserViewOnly.value && visibleWidgets.value.length >= report.value.widgets.length;
      });

      const lineChartTimeGroupingChange = (event: {
        key: string;
        value: LineChartTimeGroupingType;
      }) => onLineChartTimeGroupingChange(event.key, event.value);

      const beforeUnload = (event: Event) => event.preventDefault();

      onBeforeMount(() => {
        emitter.on('analyticsLineChartTimeGroupingChanged', lineChartTimeGroupingChange);
        window.addEventListener('beforeunload', beforeUnload);
      });

      onBeforeUnmount(() => {
        emitter.off('analyticsLineChartTimeGroupingChanged', lineChartTimeGroupingChange);
        window.removeEventListener('beforeunload', beforeUnload);
      });

      provide(reportIdFromAnalyticsPageKey, readonly(reportId));
      provide(reportFromAnalyticsPageKey, readonly(report));
      provide(selectedWidgetFromAnalyticsPageKey, readonly(selectedWidgetFilter));
      provide(isGuestFromAnalyticsPageKey, props.isGuest ?? false);

      onUnmounted(() => useFormItemsStore().$reset());

      store.$reset();

      return {
        taskGetReport,
        isSaving,
        isDeleting,
        isFilterOpen,
        isSendOpen,
        isShareOpen,
        selectedWidgetFilter,
        onTitleChange,
        onPercentageToggled,
        onDisplayChange,
        onDelete,
        onSave,
        onFilterOpen,
        onFilterClose,
        onSendOpen,
        onSendClose,
        onMetricChanged,
        onAttributeChanged,
        onAddWidgetClicked,
        onDeleteCard,
        onAddCardAfter,
        onFilterApply,
        onSaveSaved,
        onSaveCleared,
        onWidgetFilterOpen,
        hideDelete,
        report,
        canSave,
        canDelete,
        isUserViewOnly,
        canUserSeeReport,
        widgets,
        visibleWidgets,
        isAddWidgetVisible,
        isScheduleDirty,
        onShareOpen,
        onShareClose,
      };
    },
  });
